// src/cockpit4/components/ContentProtocolFlow.tsx
// ============================================
// Content Protocol 强制流程面板（第十四章《Content Protocol》）
//
// 生成流程强制（必须严格按顺序执行）：
//   ① 选择品牌 → ② 选择平台 → ③ 选择内容目标 → ④ 知识检索
//   → ⑤ Content Strategy Runtime 生成 Platform Brief（策略简报）
//   → ⑥ Platform Adapter 读取 Platform Spec 生成素材包
//   → ⑦ Marketing Runtime 发布（发布前经人工审核，Rule 03）
//
// 数据来源（禁止假数据）：
//   ContentStrategyRuntime.list_brands / list_platforms / generate_brief_and_save
//   KnowledgeRuntime.search_cards（知识检索，必须步骤）
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  ChevronRight,
  Loader2,
  RefreshCw,
  Search,
  FileText,
  Send,
  Factory,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import {
  ContentStrategyQuery,
  ContentStrategyCommand,
  ALL_CONTENT_GOALS,
} from "../../runtime/ContentStrategyRuntime";

// ============================================
// 类型
// ============================================

interface Brand {
  id: string;
  name: string;
  tagline: string;
  product: string;
}

interface Platform {
  id: string;
  name: string;
  specFile: string;
}

interface PlatformBrief {
  brand: Brand;
  platform: string;
  platformName: string;
  goal: string;
  goalLabel: string;
  persona: string;
  audience: string;
  structure: string;
  taboos: string[];
  hook: string;
  cta: string;
  tone: string;
  duration: string;
  knowledgeRefs: Array<{ title: string; path?: string }>;
  constraints: string[];
  generatedAt: string;
}

interface SearchResult {
  id: string;
  title: string;
  summary: string;
}

const STEP_FLOW: Array<{ step: number; label: string }> = [
  { step: 1, label: "品牌选择" },
  { step: 2, label: "平台选择" },
  { step: 3, label: "内容目标" },
  { step: 4, label: "知识检索" },
  { step: 5, label: "策略简报" },
  { step: 6, label: "素材生成" },
  { step: 7, label: "发布" },
];

// 双层解包 Runtime 响应：RuntimeManager 包一层 { success, data }，
// 部分 Runtime（如 ContentStrategy）还会再包一层 { success, data }，
// 因此真实数据可能在 resp.data.data，必须递归解包，避免拿到对象后 .map 崩溃。
// 统一工具函数：RuntimeManager.unwrapResponse 递归解包（P2 性能优化）
const unwrap = (resp: any): any => RuntimeManager.unwrapResponse(resp);

/** 安全提取数组（兼容直接数组 / { brands|platforms|data: [...] } / null） */
const asArray = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.brands)) return data.brands;
    if (Array.isArray(data.platforms)) return data.platforms;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.items)) return data.items;
  }
  return [];
};

// ============================================
// 主组件
// ============================================

export default function ContentProtocolFlow() {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // 流程状态（currentStep 之前必须完成）
  const [brand, setBrand] = useState<Brand | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [goal, setGoal] = useState<string>("lead_gen");
  const [topic, setTopic] = useState("");
  const [knowledgeResults, setKnowledgeResults] = useState<SearchResult[]>([]);
  const [brief, setBrief] = useState<PlatformBrief | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [generatingMaterial, setGeneratingMaterial] = useState(false);
  const [materialResult, setMaterialResult] = useState<string>("");
  const [toast, setToast] = useState<{ type: "ok" | "error"; text: string } | null>(null);

  const currentStep = useCallback((): number => {
    if (!brand) return 1;
    if (!platform) return 2;
    if (!goal) return 3;
    if (knowledgeResults.length === 0) return 4;
    if (!brief) return 5;
    return 6;
  }, [brand, platform, goal, knowledgeResults, brief]);

  // 加载品牌与平台
  const loadMeta = useCallback(async () => {
    setLoadingMeta(true);
    try {
      const [bResp, pResp] = await Promise.all([
        RuntimeManager.query(RuntimeName.ContentStrategy, {
          type: ContentStrategyQuery.ListBrands,
        } as any),
        RuntimeManager.query(RuntimeName.ContentStrategy, {
          type: ContentStrategyQuery.ListPlatforms,
        } as any),
      ]);
      // 双层解包 + 安全提取数组（避免 brands.map is not a function）
      setBrands(asArray(unwrap(bResp)) as Brand[]);
      setPlatforms(asArray(unwrap(pResp)) as Platform[]);
    } catch {
      setBrands([]);
      setPlatforms([]);
    } finally {
      setLoadingMeta(false);
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // ④ 知识检索
  const handleSearch = async () => {
    if (!topic.trim()) return;
    setSearching(true);
    try {
      const resp = await RuntimeManager.query(RuntimeName.Knowledge, {
        type: "search_cards",
        query: topic,
        limit: 5,
      } as any);
      const data = unwrap(resp);
      const cards = Array.isArray(data) ? data : data?.cards ?? [];
      const results: SearchResult[] = cards.map((c: any) => ({
        id: String(c?.id || c?.filePath || ""),
        title: String(c?.title || c?.fileName || "知识卡片"),
        summary: String(c?.summary || c?.content || "").slice(0, 80),
      }));
      setKnowledgeResults(results);
      if (results.length === 0) {
        setToast({ type: "error", text: "未检索到相关知识，请换个关键词" });
      } else {
        setToast({ type: "ok", text: `已检索到 ${results.length} 条知识` });
      }
    } catch (err: any) {
      setToast({ type: "error", text: `知识检索失败：${err?.message || String(err)}` });
    } finally {
      setSearching(false);
    }
  };

  // ⑤ 生成策略简报
  const handleGenerateBrief = async () => {
    if (!brand || !platform) return;
    setBriefLoading(true);
    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.ContentStrategy,
        ContentStrategyCommand.GenerateBriefAndSave,
        { brand: brand.id, platform: platform.id, goal, topic: topic || brand.product }
      );
      // 双层解包：RuntimeManager 包一层，ContentStrategy 再包一层 → brief 在 resp.data.data.brief
      const innerResult = resp?.data && typeof resp.data === "object" && "data" in resp.data
        ? resp.data as any
        : null;
      const briefData = innerResult?.data ?? resp?.data;
      if (resp?.success && briefData?.brief) {
        setBrief(briefData.brief as PlatformBrief);
        setToast({ type: "ok", text: "✅ 策略简报已生成并写入 XEAOS/Daily/Brief-*.md" });
      } else {
        setToast({ type: "error", text: `生成失败：${briefData?.error || resp?.error || "未知错误"}` });
      }
    } catch (err: any) {
      setToast({ type: "error", text: `生成失败：${err?.message || String(err)}` });
    } finally {
      setBriefLoading(false);
    }
  };
  // ⑥ 素材生成：将策略简报直接接入 MediaStudio 实际生成调用（按简报约束生产）
  const handleGenerateMaterials = async () => {
    if (!brief) return;
    setGeneratingMaterial(true);
    setMaterialResult("");
    try {
      // 将简报约束注入描述，作为实际生成提示词的硬约束（Content Protocol：按平台逻辑生产不同产品）
      const description = [
        brief.persona,
        `受众：${brief.audience}`,
        `结构：${brief.structure}`,
        `语气：${brief.tone}`,
        `时长/字数：${brief.duration}`,
        `钩子：${brief.hook}`,
        `CTA：${brief.cta}`,
        brief.taboos.length > 0 ? `禁忌：${brief.taboos.join("、")}` : "",
        `GEO/SEO 关键词植入：${Array.isArray((brief.brand as any)?.keywords)
          ? (brief.brand as any).keywords.slice(0, 5).join("、")
          : ""}`,
      ].filter(Boolean).join("\n");

      // 平台直接透传（douyin_enterprise / douyin_personal / xiaohongshu / wechat / zhihu / bilibili 均受支持）
      const resp = await RuntimeManager.execute(RuntimeName.MediaStudio, "generate_script", {
        platform: brief.platform,
        topic: topic || brief.brand.product,
        description,
        style: "科技感深色背景，品牌化风格",
      } as any);

      if (resp?.success && resp.data) {
        const pkg = resp.data as any;
        const script =
          pkg?.articleScript?.content ||
          pkg?.douyinScript?.content ||
          pkg?.videoScript?.content ||
          pkg?.xiaohongshuScript?.content ||
          pkg?.liveStreamScript?.content ||
          "";
        setMaterialResult(script || JSON.stringify(pkg, null, 2).slice(0, 3000));
        setToast({ type: "ok", text: `✅ 素材已生成（${brief.platformName}），已按策略简报约束生产` });
      } else {
        setToast({ type: "error", text: `素材生成失败：${resp?.error || "未知错误"}` });
      }
    } catch (err: any) {
      setToast({ type: "error", text: `素材生成失败：${err?.message || String(err)}` });
    } finally {
      setGeneratingMaterial(false);
    }
  };


  return (
    <div className="space-y-3">
      {/* 流程步骤条 */}
      <div className="flex items-center gap-1 flex-wrap">
        {STEP_FLOW.map((s) => {
          const done = s.step < currentStep();
          const active = s.step === currentStep();
          return (
            <div key={s.step} className="flex items-center gap-1">
              <span
                className={`px-2 py-1 rounded-md text-[9px] font-mono border ${
                  done
                    ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                    : active
                    ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300"
                    : "bg-white/[0.02] border-white/[0.06] text-gray-500"
                }`}
              >
                {done ? "✓ " : ""}{s.step}. {s.label}
              </span>
              {s.step < 7 && <ChevronRight size={10} className="text-gray-600" />}
            </div>
          );
        })}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`px-3 py-2 rounded-lg border text-[11px] font-mono ${
              toast.type === "ok"
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                : "bg-red-500/10 border-red-500/30 text-red-300"
            }`}
          >
            {toast.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ① 品牌选择 */}
      <div className="px-3 py-2.5 rounded-lg border border-white/[0.05] bg-white/[0.02]">
        <div className="text-[10px] font-mono text-gray-400 uppercase tracking-wider mb-2">
          ① 选择品牌
        </div>
        {loadingMeta ? (
          <div className="text-[10px] font-mono text-gray-500">加载品牌库...</div>
        ) : (brands || []).length === 0 ? (
          <div className="text-[10px] font-mono text-gray-500">品牌库不可用</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {(brands || []).map((b) => (
              <button
                key={b.id}
                onClick={() => { setBrand(b); setBrief(null); }}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-mono border transition-colors ${
                  brand?.id === b.id
                    ? "bg-cyan-500/20 border-cyan-500/40 text-cyan-300"
                    : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-gray-200"
                }`}
              >
                {b.name}
                <span className="block text-[8px] text-gray-500">{b.tagline}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ② 平台选择 */}
      {brand && (
        <div className="px-3 py-2.5 rounded-lg border border-white/[0.05] bg-white/[0.02]">
          <div className="text-[10px] font-mono text-gray-400 uppercase tracking-wider mb-2">
            ② 选择平台
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(platforms || []).map((p) => (
              <button
                key={p.id}
                onClick={() => { setPlatform(p); setBrief(null); }}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-mono border transition-colors ${
                  platform?.id === p.id
                    ? "bg-purple-500/20 border-purple-500/40 text-purple-300"
                    : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-gray-200"
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ③ 内容目标 */}
      {platform && (
        <div className="px-3 py-2.5 rounded-lg border border-white/[0.05] bg-white/[0.02]">
          <div className="text-[10px] font-mono text-gray-400 uppercase tracking-wider mb-2">
            ③ 选择内容目标
          </div>
          <div className="flex flex-wrap gap-1.5">
            {ALL_CONTENT_GOALS.map((g) => (
              <button
                key={g.value}
                onClick={() => { setGoal(g.value); setBrief(null); }}
                className={`px-3 py-1 rounded-lg text-[10px] font-mono border transition-colors ${
                  goal === g.value
                    ? "bg-amber-500/20 border-amber-500/40 text-amber-300"
                    : "bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-gray-200"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ④ 知识检索（必须） */}
      {goal && (
        <div className="px-3 py-2.5 rounded-lg border border-white/[0.05] bg-white/[0.02]">
          <div className="text-[10px] font-mono text-gray-400 uppercase tracking-wider mb-2">
            ④ 知识检索（必须）
          </div>
          <div className="flex gap-2">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="输入主题，如：AI 企业操作系统 / 酒店数字化"
              className="flex-1 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06] text-[11px] font-mono text-gray-200 focus:outline-none focus:border-cyan-500/40 placeholder:text-gray-600"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !topic.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 disabled:opacity-50 transition-colors"
            >
              {searching ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
              检索知识库
            </button>
          </div>
          {knowledgeResults.length > 0 && (
            <div className="mt-2 space-y-1">
              {knowledgeResults.map((r) => (
                <div key={r.id} className="px-2 py-1.5 rounded bg-white/[0.03] border border-white/[0.05]">
                  <div className="text-[10px] font-mono text-gray-300">{r.title}</div>
                  {r.summary && <div className="text-[9px] font-mono text-gray-500">{r.summary}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ⑤ 生成策略简报 */}
      {knowledgeResults.length > 0 && (
        <div className="px-3 py-2.5 rounded-lg border border-purple-500/20 bg-purple-500/[0.04]">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-mono text-purple-300 uppercase tracking-wider">
              ⑤ 生成策略简报（Platform Brief）
            </div>
            <button
              onClick={handleGenerateBrief}
              disabled={briefLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono bg-purple-500/15 border border-purple-500/30 text-purple-300 hover:bg-purple-500/25 disabled:opacity-50 transition-colors"
            >
              {briefLoading ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />}
              生成简报
            </button>
          </div>
          {brief && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-mono text-gray-200">
                {brief.brand.name} × {brief.platformName}（{brief.goalLabel}）
              </div>
              <div className="text-[10px] font-mono text-gray-400">人设：{brief.persona}</div>
              <div className="text-[10px] font-mono text-gray-400">受众：{brief.audience}</div>
              <div className="text-[10px] font-mono text-gray-400">结构：{brief.structure}</div>
              <div className="text-[10px] font-mono text-gray-400">语气：{brief.tone} · {brief.duration}</div>
              <div className="text-[10px] font-mono text-gray-400">钩子：{brief.hook}</div>
              <div className="text-[10px] font-mono text-gray-400">CTA：{brief.cta}</div>
              {brief.taboos.length > 0 && (
                <div className="text-[10px] font-mono text-red-300/80">
                  禁忌：{brief.taboos.join("、")}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ⑥ 素材生成 + ⑦ 发布（跳到内容工厂生成素材，发布需人工审核 Rule 03） */}
      {brief && (
        <div className="px-3 py-2.5 rounded-lg border border-white/[0.05] bg-white/[0.02]">
          <div className="flex items-center gap-1.5 mb-2">
            <Factory size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">
              ⑥ 素材生成 → ⑦ 发布
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={handleGenerateMaterials}
              disabled={generatingMaterial}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-mono bg-cyan-500/15 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/25 disabled:opacity-50 transition-colors"
            >
              {generatingMaterial ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
              {generatingMaterial ? "生成中..." : "生成素材（按策略简报）"}
            </button>
            <span className="text-[9px] font-mono text-gray-600">
              发布前必须经人工审核（Rule 03：AI 不能绕过人工审核直接发布）
            </span>
          </div>
          {materialResult && (
            <div className="mt-2 rounded-lg bg-black/30 border border-white/[0.06] overflow-hidden">
              <div className="px-2.5 py-1.5 border-b border-white/[0.05] text-[9px] font-mono text-cyan-400 uppercase tracking-wider">
                生成素材（{brief.platformName} · {brief.goalLabel}）
              </div>
              <pre className="max-h-[260px] overflow-y-auto p-3 text-[10px] font-mono text-gray-300 whitespace-pre-wrap break-words">
                {materialResult.slice(0, 6000)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
